export const VIEW_TYPES = {
  MONTH: 'MES',
  WEEK: 'SEMANA',
  DAY: 'DÍA',
};

export const TURN_TYPES = {
  SESION_PACIENTE: 'SESION_PACIENTE',
  REUNION_PADRES: 'REUNION_PADRES',
  REUNION_COLEGIO: 'REUNION_COLEGIO',
  REUNION_PROFESIONALES: 'REUNION_PROFESIONALES'
};

export const TURN_TYPE_OPTIONS = [
  {
    value: TURN_TYPES.SESION_PACIENTE,
    label: 'Sesión con paciente'
  },
  {
    value: TURN_TYPES.REUNION_PADRES,
    label: 'Reunión con padres'
  },
  {
    value: TURN_TYPES.REUNION_COLEGIO,
    label: 'Reunión con colegio'
  },
  {
    value: TURN_TYPES.REUNION_PROFESIONALES,
    label: 'Reunión con profesionales'
  }
];

// Normaliza turnos viejos o datos incompletos.
// Si el backend no manda tipo_turno por compatibilidad, lo tratamos como sesión con paciente.
export const normalizeTurnType = (tipoTurno) => {
  const validTurnTypes = Object.values(TURN_TYPES);

  if (validTurnTypes.includes(tipoTurno)) {
    return tipoTurno;
  }

  return TURN_TYPES.SESION_PACIENTE;
};

export const getTurnTypeLabel = (tipoTurno) => {
  const normalizedTurnType = normalizeTurnType(tipoTurno);

  const option = TURN_TYPE_OPTIONS.find(
    (turnTypeOption) => turnTypeOption.value === normalizedTurnType
  );

  return option?.label || 'Sesión con paciente';
};

export const CALENDAR_TURN_COLORS = {
  SESION_PACIENTE_CONSULTORIO: '#23AAAF',
  SESION_PACIENTE_DOMICILIO: '#655EE8',
  REUNION_PADRES: '#D76970',
  REUNION_COLEGIO: '#EFC340',
  REUNION_PROFESIONALES: '#FC8906',
  FALLBACK: '#646464'
};

export const getAppointmentCalendarColor = (appointment) => {
  const tipoTurno = normalizeTurnType(appointment?.tipo_turno);

  if (tipoTurno === TURN_TYPES.SESION_PACIENTE) {
    return CALENDAR_TURN_COLORS.SESION_PACIENTE_CONSULTORIO;
  }

  if (tipoTurno === TURN_TYPES.REUNION_PADRES) {
    return CALENDAR_TURN_COLORS.REUNION_PADRES;
  }

  if (tipoTurno === TURN_TYPES.REUNION_COLEGIO) {
    return CALENDAR_TURN_COLORS.REUNION_COLEGIO;
  }

  if (tipoTurno === TURN_TYPES.REUNION_PROFESIONALES) {
    return CALENDAR_TURN_COLORS.REUNION_PROFESIONALES;
  }

  return CALENDAR_TURN_COLORS.FALLBACK;
};

export const getAppointmentCalendarLabel = (appointment) => {
  const tipoTurno = normalizeTurnType(appointment?.tipo_turno);

  if (tipoTurno === TURN_TYPES.SESION_PACIENTE) {
    if (
      appointment?.modalidad_turno === 'presencial' &&
      appointment?.consultorio_nombre
    ) {
      return `Sesión con paciente · ${appointment.consultorio_nombre}`;
    }

    return 'Sesión con paciente';
  }

  return getTurnTypeLabel(tipoTurno);
};

export const getAppointmentCalendarTooltip = (appointment) => {
  const label = getAppointmentCalendarLabel(appointment);
  const modalidad = appointment?.modalidad_turno === 'virtual'
    ? 'Virtual'
    : 'Presencial';

  return `${label}. Modalidad: ${modalidad}.`;
};

export const getCalendarLegendItems = () => {
  return [
    {
      key: 'sesion-paciente',
      color: CALENDAR_TURN_COLORS.SESION_PACIENTE_CONSULTORIO,
      title: 'Sesión con paciente',
      description: 'Turno asociado al paciente'
    },
    {
      key: 'reunion-padres',
      color: CALENDAR_TURN_COLORS.REUNION_PADRES,
      title: 'Reunión con padres',
      description: 'Encuentro asociado al paciente'
    },
    {
      key: 'reunion-colegio',
      color: CALENDAR_TURN_COLORS.REUNION_COLEGIO,
      title: 'Reunión con colegio',
      description: 'Articulación con escuela o colegio'
    },
    {
      key: 'reunion-profesionales',
      color: CALENDAR_TURN_COLORS.REUNION_PROFESIONALES,
      title: 'Reunión con profesionales',
      description: 'Intercambio con otros profesionales'
    },
    {
      key: 'email-problema',
      color: '#B91C1C',
      title: '✉! Problema de email',
      description: 'Rebotado, spam o error de envío'
    }
  ];
};

export const DAY_TIME_SLOTS = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', 
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', 
  '20:00', '21:00', '22:00', '23:00'
];

export const WEEKDAY_LABELS = ['LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO', 'DOMINGO'];
export const WEEKDAY_SHORT_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

// Mock data con estructura real del backend
export const CITAS_MOCK = [
  {
    id: 1,
    fecha: "2025-09-29",
    hora_inicio: "09:00",
    hora_fin: "09:45",
    paciente_id: 201,
    paciente_nombre: "Juan Pérez",
    lugar: ":hospital: Físico",
    link_reunion: null,
    cud: false,
    forma_pago: "efectivo",
    abonado: "pago",
    valor_sesion: 5000,
    estado_cita: "no asistió",
    repetir_turno: 'semanalmente'
  },
  {
    id: 2,
    fecha: "2025-09-29",
    hora_inicio: "10:00",
    hora_fin: "10:45",
    paciente_id: 202,
    paciente_nombre: "María Gómez",
    lugar: ":computer: Online",
    link_reunion: "https://meet.google.com/abc-defg-hij",
    cud: true,
    forma_pago: "transferencia",
    abonado: "pago",
    valor_sesion: 5000,
    estado_cita: "asistió",
    repetir_turno: false
  },
  {
    id: 3,
    fecha: "2025-09-30",
    hora_inicio: "11:00",
    hora_fin: "11:45",
    paciente_id: 203,
    paciente_nombre: "Carlos López",
    lugar: ":hospital: Físico",
    link_reunion: null,
    cud: false,
    forma_pago: "tarjeta",
    abonado: "no pago",
    valor_sesion: 15000,
    estado_cita: "no asistió",
    repetir_turno: "mensualmente"
  },
  {
    id: 4,
    fecha: "2025-09-30",
    hora_inicio: "14:00",
    hora_fin: "14:45",
    paciente_id: 204,
    paciente_nombre: "Ana Martínez",
    lugar: ":computer: Online",
    link_reunion: "https://zoom.us/j/123456789",
    cud: true,
    forma_pago: "efectivo",
    abonado: "pago",
    valor_sesion: 20000,
    estado_cita: "asistió",
    repetir_turno: "quincenalmente"
  },
  {
    id: 5,
    fecha: "2025-09-30",
    hora_inicio: "15:00",
    hora_fin: "15:45",
    paciente_id: 205,
    paciente_nombre: "Roberto Silva",
    lugar: ":hospital: Físico",
    link_reunion: null,
    cud: false,
    forma_pago: "transferencia",
    abonado: "pago",
    valor_sesion: 15000,
    estado_cita: "pendiente",
    repetir_turno: false
  },
  {
    id: 14,
    fecha: "2025-09-30",
    hora_inicio: "16:00",
    hora_fin: "16:15",
    paciente_id: 305,
    paciente_nombre: "Maria Candelero",
    lugar: ":hospital: Físico",
    link_reunion: null,
    cud: false,
    forma_pago: "transferencia",
    abonado: "pago",
    valor_sesion: 25000,
    estado_cita: "pendiente",
    repetir_turno: "quincenalmente"
  },
  {
    id: 6,
    fecha: "2025-10-01",
    hora_inicio: "16:00",
    hora_fin: "16:45",
    paciente_id: 206,
    paciente_nombre: "Laura Fernández",
    lugar: ":hospital: Físico",
    link_reunion: null,
    cud: false,
    forma_pago: "tarjeta",
    abonado: "no pago",
    valor_sesion: 5000,
    estado_cita: "pendiente",
    repetir_turno: "quincenalmente"
  },
  {
    id: 7,
    fecha: "2025-10-01",
    hora_inicio: "17:00",
    hora_fin: "17:45",
    paciente_id: 207,
    paciente_nombre: "Diego Rodríguez",
    lugar: ":computer: Online",
    link_reunion: "https://meet.google.com/xyz-1234-abc",
    cud: true,
    forma_pago: "efectivo",
    abonado: "pago",
    valor_sesion: 5000,
    estado_cita: "pendiente",
    repetir_turno: false
  },
  {
    id: 8,
    fecha: "2025-10-02",
    hora_inicio: "18:00",
    hora_fin: "18:45",
    paciente_id: 208,
    paciente_nombre: "Carmen Vásquez",
    lugar: ":hospital: Físico",
    link_reunion: null,
    cud: false,
    forma_pago: "transferencia",
    abonado: "pago",
    valor_sesion: 5000,
    estado_cita: "no asistió",
    repetir_turno: false
  },
  {
    id: 9,
    fecha: "2025-10-03",
    hora_inicio: "07:00",
    hora_fin: "07:45",
    paciente_id: 209,
    paciente_nombre: "Pedro Martínez",
    lugar: ":computer: Online",
    link_reunion: "https://meet.google.com/early-123",
    cud: true,
    forma_pago: "efectivo",
    abonado: "pago",
    valor_sesion: 5000,
    estado_cita: "pendiente",
    repetir_turno: false
  },
  {
    id: 10,
    fecha: "2025-10-02",
    hora_inicio: "20:00",
    hora_fin: "20:45",
    paciente_id: 210,
    paciente_nombre: "Sofia López",
    lugar: ":hospital: Físico",
    link_reunion: null,
    cud: false,
    forma_pago: "tarjeta",
    abonado: "no pago",
    valor_sesion: 5000,
    estado_cita: "pendiente",
    repetir_turno: false
  },
  {
    id: 11,
    fecha: "2025-10-04",
    hora_inicio: "22:00",
    hora_fin: "22:45",
    paciente_id: 211,
    paciente_nombre: "Miguel Torres",
    lugar: ":computer: Online",
    link_reunion: "https://zoom.us/j/late-night",
    cud: true,
    forma_pago: "transferencia",
    abonado: "pago",
    valor_sesion: 5000,
    estado_cita: "asistió",
    repetir_turno: "semanalmente"
  },
  {
    id: 12,
    fecha: "2025-10-05",
    hora_inicio: "17:15",
    hora_fin: "17:45",
    paciente_id: 302,
    paciente_nombre: "Lourdes Lanciani",
    lugar: ":computer: Online",
    link_reunion: "https://meet.google.com/abc-defg-hij",
    cud: true,
    forma_pago: "transferencia",
    abonado: "pago",
    valor_sesion: 15000,
    estado_cita: "ausente con aviso",
    repetir_turno: "mensualmente"
  },
  {
    id: 13,
    fecha: "2025-10-05",
    hora_inicio: "18:30",
    hora_fin: "18:45",
    paciente_id: 302,
    paciente_nombre: "Lourdes Lanciani",
    lugar: ":computer: Online",
    link_reunion: "https://meet.google.com/abc-defg-hij",
    cud: true,
    forma_pago: "transferencia",
    abonado: "pago",
    valor_sesion: 15000,
    estado_cita: "pendiente",
    repetir_turno: "mensualmente"
  },
];

// Estados de cita disponibles
export const ESTADOS_CITA = {
  PENDIENTE: 'pendiente',
  ASISTIO: 'asistió',
  NO_ASISTIO: 'no asistió'
};

// Formas de pago disponibles
export const FORMAS_PAGO = {
  EFECTIVO: 'efectivo',
  TRANSFERENCIA: 'transferencia',
  TARJETA: 'tarjeta'
};

// Estados de pago
export const ESTADOS_PAGO = {
  PAGO: 'pago',
  NO_PAGO: 'no pago'
};

// Tipos de lugar
export const TIPOS_LUGAR = {
  FISICO: 'Físico',
  ONLINE: 'Online'
};